/** 32-bit left rotate. `>>> 0` keeps everything an unsigned 32-bit int. */
const rotl = (x, k) => ((x << k) | (x >>> (32 - k))) >>> 0;
/** cyrb128 - a well-known public-domain string hash that expands a seed into four 32-bit words. */
const cyrb128 = (str) => {
    let h1 = 1779033703;
    let h2 = 3144134277;
    let h3 = 1013904242;
    let h4 = 2773480762;
    for (let i = 0; i < str.length; i++) {
        const k = str.charCodeAt(i);
        h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
        h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
        h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
        h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
    }
    h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
    h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
    h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
    h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
    h1 ^= h2 ^ h3 ^ h4;
    h2 ^= h1;
    h3 ^= h1;
    h4 ^= h1;
    return [h1 >>> 0, h2 >>> 0, h3 >>> 0, h4 >>> 0];
};
/** Build an Rng from four 32-bit state words. */
const fromState = (s0, s1, s2, s3) => {
    let a = s0 >>> 0;
    let b = s1 >>> 0;
    let c = s2 >>> 0;
    let d = s3 >>> 0;
    // xoshiro128** must not be seeded to the all-zero state.
    if ((a | b | c | d) === 0)
        a = 1;
    const next = () => {
        const result = Math.imul(rotl(Math.imul(b, 5) >>> 0, 7), 9) >>> 0;
        const t = (b << 9) >>> 0;
        c ^= a;
        d ^= b;
        b ^= c;
        a ^= d;
        c ^= t;
        d = rotl(d, 11);
        a >>>= 0;
        b >>>= 0;
        c >>>= 0;
        d >>>= 0;
        return result / 4294967296; // [0, 1)
    };
    const fork = (label) => {
        // Child seed = parent's CURRENT state XOR the label's hash. Pure in (state, label);
        // parent stream is untouched.
        const [f0, f1, f2, f3] = cyrb128(label);
        return fromState(a ^ f0, b ^ f1, c ^ f2, d ^ f3);
    };
    return { next, fork };
};
/** Create a deterministic Rng from a seed string. */
export const createRng = (seed) => {
    const [s0, s1, s2, s3] = cyrb128(seed);
    return fromState(s0, s1, s2, s3);
};
/** Integer in [0, n) from an Rng. Uniform enough for shuffling/sampling at our scale. */
export const nextInt = (rng, n) => Math.floor(rng.next() * n);
//# sourceMappingURL=rng.js.map